import { LineChart } from "echarts/charts";
import { GridComponent, TitleComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { memo, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Spinner } from "@/components/ui/spinner";
import type { TimeAxisPanelSpec } from "../ChartSpec";
import { useHoverTime } from "../crosshair/use-hover-time";
import { useCrosshairBus, useDataViewerStore } from "../data-viewer-provider";
import { pickResolution } from "../resolution/pick-resolution";
import type { RequestedStream } from "../stream-data/types";
import { useEnsureStreamData } from "../stream-data/use-ensure-stream-data";
import { useStreamData } from "../stream-data/use-stream-data";
import { useGetDisplayInfo } from "../units/use-get-display-info";
import { buildTimePanelOptions } from "./build-time-panel-options";
import { PanelLegend } from "./panel-legend";
import { type GetConversion, prepareSeries } from "./prepare-series";
import { PANEL_WIDTH_GRIP_RESERVE_PX, ResizerGrip } from "./resize/resizer-grip";
import { usePanelResize } from "./resize/use-panel-resize";
import { PanelUnitPicker } from "./unit-picker/panel-unit-picker";
import { useAxisNameUnitPicker } from "./unit-picker/use-axis-name-unit-picker";

const DEFAULT_PANEL_HEIGHT_PX = 200;
const DEFAULT_PANEL_ROW_GAP_PX = 4;

// Offset resizer grips past axis labels / y-axis name width.
const HEIGHT_RESIZER_GRIP_OFFSET_PX = 42;
const WIDTH_RESIZER_GRIP_OFFSET_PERCENT = "-8%";

echarts.use([LineChart, TitleComponent, TooltipComponent, GridComponent, CanvasRenderer]);

// Skip re-render when another panel's spec changes — this panel's props are unchanged.
export const Panel = memo(
    ({
        spec,
        gridMargins,
        maxStackWidth,
        syncCrosshair = true,
        emitsHoverTime = true,
    }: {
        spec: TimeAxisPanelSpec;
        gridMargins: { left: number; right: number };
        maxStackWidth: number;
        syncCrosshair?: boolean;
        emitsHoverTime?: boolean;
    }) => {
        const chartRef = useRef<HTMLDivElement | null>(null);
        const crosshairBus = useCrosshairBus();
        const hoverTime = useHoverTime();

        const startMs = useDataViewerStore((state) => state.startMs);
        const endMs = useDataViewerStore((state) => state.endMs);

        const maxWidth = Math.max(0, maxStackWidth - PANEL_WIDTH_GRIP_RESERVE_PX - DEFAULT_PANEL_ROW_GAP_PX);
        const { size, heightHandleProps, widthHandleProps } = usePanelResize({
            maxWidth,
            defaultHeight: DEFAULT_PANEL_HEIGHT_PX,
        });
        const resolution = pickResolution(size.width, startMs, endMs);

        const setPanelResolution = useDataViewerStore((state) => state.setPanelResolution);
        useEffect(() => {
            setPanelResolution(spec.id, resolution);
        }, [spec.id, resolution, setPanelResolution]);

        const getDisplayInfo = useGetDisplayInfo();
        const {
            pickerState,
            onPickerStateChange,
            enable: enableUnitPicker,
            disable: disableUnitPicker,
        } = useAxisNameUnitPicker(spec, getDisplayInfo);

        // Strip display fields — fetch hooks only need streamId.
        const requestedStreams = useMemo<RequestedStream[]>(
            () => spec.streams.map((stream) => ({ streamId: stream.streamId })),
            [spec.streams],
        );

        // Kick off a fetch for any missing data; read windowed points from cache. See stream-data/README.md.
        const isFetching = useEnsureStreamData({ streams: requestedStreams, startMs, endMs, resolution });
        const data = useStreamData({ streams: requestedStreams, startMs, endMs, resolution });

        // Spinner only while fetching AND at least one stream has no data yet.
        // Panning into cached range shows data immediately; multi-stream waits for all.
        const isLoadingStreamData = isFetching && requestedStreams.some((stream) => !(stream.streamId in data));

        // Join cached points with spec (color, name, axis) and convert to display units.
        const preparedSeries = useMemo(() => {
            const getConversion: GetConversion = (unitCategory) => getDisplayInfo(unitCategory).fromDatabase;
            return prepareSeries(data, spec.streams, spec.axes, getConversion);
        }, [data, spec.streams, spec.axes, getDisplayInfo]);

        const chartOptions = useMemo(
            () => buildTimePanelOptions(spec, preparedSeries, getDisplayInfo, gridMargins, { startMs, endMs }),
            [spec, preparedSeries, getDisplayInfo, gridMargins, startMs, endMs],
        );

        // Ref so the init effect reads latest options without re-running.
        const chartOptionsRef = useRef(chartOptions);
        chartOptionsRef.current = chartOptions;

        // Init chart once on mount. Option updates go through setOption below.
        useLayoutEffect(() => {
            const chartContainer = chartRef.current;
            if (!chartContainer) return undefined;

            const existing = echarts.getInstanceByDom(chartContainer);
            if (existing) {
                crosshairBus.unregister(existing);
                existing.dispose();
            }

            const chart = echarts.init(chartContainer);
            chart.setOption(chartOptionsRef.current);

            if (syncCrosshair) crosshairBus.register(chart);
            if (emitsHoverTime) hoverTime.enable(chart);
            enableUnitPicker(chart, chartContainer);

            return () => {
                disableUnitPicker();
                hoverTime.disable();
                crosshairBus.unregister(chart);
                chart.dispose();
            };
        }, [syncCrosshair, emitsHoverTime, crosshairBus, hoverTime, enableUnitPicker, disableUnitPicker]);

        useEffect(() => {
            if (chartRef.current) {
                echarts.getInstanceByDom(chartRef.current)?.resize();
            }
        }, [size.height, size.width]);

        // Push option changes without tearing down the chart instance.
        // replaceMerge: panel id is stable across zones (category-panel-*), so a shrinking stream
        // list must replace the whole series array — default merge leaves orphaned series behind.
        useEffect(() => {
            if (chartRef.current) {
                echarts.getInstanceByDom(chartRef.current)?.setOption(chartOptions, { replaceMerge: ["series"] });
            }
        }, [chartOptions]);

        const noDataForRange =
            requestedStreams.length > 0 && preparedSeries.every((series) => series.points.length === 0);

        return (
            <div className={"mb-2 w-full"}>
                <div className={"inline-flex max-w-full items-center gap-1"}>
                    <div className={"flex flex-col items-center gap-1"}>
                        <div className={"flex shrink-0 flex-col"} style={{ width: size.width }}>
                            <div className={"relative shrink-0"} style={{ height: size.height }}>
                                <div ref={chartRef} className={"size-full"} />

                                {!isLoadingStreamData && noDataForRange && (
                                    <div
                                        className={"absolute inset-0 flex items-center justify-center bg-background/60"}
                                    >
                                        <div className={"text-sm text-muted-foreground"}>No data for range</div>
                                    </div>
                                )}
                                {isLoadingStreamData ? (
                                    <div
                                        className={"absolute inset-0 flex items-center justify-center bg-background/60"}
                                    >
                                        <Spinner className={"size-6 text-muted-foreground"} />
                                    </div>
                                ) : null}
                                <PanelUnitPicker
                                    pickerState={pickerState}
                                    onPickerStateChange={onPickerStateChange}
                                    scrollAnchorRef={chartRef}
                                />
                            </div>
                            <PanelLegend series={preparedSeries} />
                        </div>
                        <ResizerGrip
                            orientation={"horizontal"}
                            handleProps={heightHandleProps}
                            offset={HEIGHT_RESIZER_GRIP_OFFSET_PX}
                        />
                    </div>
                    <ResizerGrip
                        orientation={"vertical"}
                        handleProps={widthHandleProps}
                        offset={WIDTH_RESIZER_GRIP_OFFSET_PERCENT}
                    />
                </div>
            </div>
        );
    },
);
Panel.displayName = "Panel";
